import { useEffect, useState } from 'react'
import type { Api } from '../api'
import { ApiError, NO_RECV_FILTERS, USD, isConverted } from '../api/types'
import type { ApiFailure, Currency, MatrixNode, RecvFilters } from '../api/types'
import { Loading, StatTile, compact, money, currencyShort } from './ui'
import { PARTIAL_TITLE, WITHHELD, WITHHELD_TITLE, sumShown, type ShownTotal } from '../api/answer'
import { FailureState } from './FailureState'

/**
 * The KPI strip above the aging pages.
 *
 * Four figures, taken from the deployed Fabric app's KpiStrip: total
 * receivables, total overdue, aged over 120 days, advance payments.
 *
 * ---------------------------------------------------------------------------
 * NO NEW NUMBERS
 *
 * Every figure here is a sum of the SAME top-level rows the matrix below it
 * shows — the strip re-uses the matrix's own root query rather than asking a
 * separate question. That is the whole discipline of a KPI strip on a report: a
 * headline computed by a different path than the table under it will disagree
 * with that table one day, and the reader will believe whichever they saw first.
 *
 * ---------------------------------------------------------------------------
 * A MASKED MEASURE MAKES ITS KPI UNKNOWN, NOT ZERO
 *
 * The column mask is per-CALLER, not per-row: a measure the caller may not see
 * is null on every row. So a KPI over a masked measure is not a sum of zeros —
 * it is unknown, and it says so. Summing the visible half would produce a
 * confident number that reconciles against nothing.
 *
 * UNDER THE NEW MODEL the mask is per piece of access, so a figure can be shown
 * on some rows and withheld on others, and each row's Partial says which of its
 * figures leave hidden values out (M8). A KPI is then the sum of what shows,
 * marked as partly withheld, and withheld only when no row shows it: "unknown"
 * would blank the strip for every reader whose access is not uniform.
 */
export function KpiStrip({
  api,
  viewAs = null,
  // Defaults to USD with the pages (23 Sep 2026); every parent passes it.
  currency = USD,
  filters = NO_RECV_FILTERS,
}: {
  api: Api
  viewAs?: string | null
  currency?: Currency
  /** The page's standing filter bar. The strip rides the SAME root call the
      matrix makes, so passing it here is what keeps the headline and the grid
      under it describing one population rather than two. */
  filters?: RecvFilters
}) {
  const [rows, setRows] = useState<MatrixNode[] | null>(null)
  /** The new permission model answered (M8). */
  const [v2, setV2] = useState(false)
  const [failure, setFailure] = useState<ApiFailure | null>(null)
  /** Bumped by "Try again" to re-run the effect with the same inputs. */
  const [nonce, setNonce] = useState(0)

  useEffect(() => {
    let cancelled = false
    setRows(null)
    setFailure(null)
    // The same root call the matrix makes. Both are read-only and the browser
    // caches nothing, so this is one extra request per page view — the price of
    // the strip and the table never disagreeing.
    // `currency` MUST be in the request, not only in the dependency array.
    //
    // It was in the deps and not in the call, so toggling to USD re-fetched --
    // Loading, network request, everything looked right -- and asked the exact
    // same question, giving the exact same local figures. The headline read
    // 45.34B under a control set to USD; the real dollar book is 154.8M.
    //
    // A dependency array that lists a value the request ignores is worse than
    // omitting it: the refetch is the thing that makes it look wired up.
    api
      .receivablesMatrix({}, { currency, filters })
      .then((r) => { if (!cancelled) { setRows(r.data); setV2(r.vis !== null) } })
      .catch((e: unknown) => {
        if (cancelled) return
        setFailure(e instanceof ApiError ? e.failure : { kind: 'error', title: 'Unexpected error.' })
      })
    return () => {
      cancelled = true
    }
  }, [api, viewAs, currency, filters, nonce])

  /* A FAILURE IS NOT AN EMPTY BOOK. Until 22 Sep 2026 every error was swallowed
     into `setRows([])` and the strip returned null, so a refusal, a 500 and a
     slow call all looked the same: an empty "Key figures" bar. On By
     Transaction the grid uses a different endpoint, so a 500 here left four
     tiles missing with nothing to say why. A failure is now kept, and said —
     here if it is the strip's own, by the grid below if it is the page's. */
  /* BUT A REFUSAL IS THE PAGE'S TO SAY, ONCE. A refusal, a maintenance window
     or a lapsed sign-in applies to the whole report, and the grid below this
     strip is refused by the same gates and already shows the panel. Rendering
     it here as well stacked two identical panels with two different
     references for one event — on Receivables, the screen that was otherwise
     right. So the strip stays silent for those and speaks only for a failure
     that is genuinely its own: an error on the one request it makes, which on
     By Transaction is not the grid's endpoint and would otherwise leave four
     tiles missing with nothing to say why. */
  /* THE SAME RULE AS SALES' STRIP, WORD FOR WORD: only a genuine error is said
     here. The two strips briefly disagreed during this fix — Sales explained a
     refusal once and Receivables twice — and a reader holding both reports
     should not learn two behaviours for one situation. */
  if (failure) {
    return failure.kind === 'error'
      ? <FailureState failure={failure} onRetry={() => setNonce((n) => n + 1)} />
      : null
  }

  // While it loads, say so -- the same standard loader, and the strip keeps its
  // height so the grid below does not jump. See SalesKpis (16 Sep 2026).
  if (!rows) {
    return (
      <div className="stat-strip">
        <div className="min-h-[4.5rem] flex-1">
          <Loading label="Loading key figures" />
        </div>
      </div>
    )
  }

  // A genuinely empty book. Reached only on success now.
  if (rows.length === 0) return null

  /** Today: null if ANY row is masked — the total is then genuinely unknown.
      Under the new model: the sum of what shows, marked (sumShown). */
  const sum = (...parts: [field: string, pick: (n: MatrixNode) => number | null][]): ShownTotal => {
    if (v2) return sumShown(rows, ...parts)
    if (rows.some((r) => parts.some(([, pick]) => pick(r) === null))) return { value: null, partial: false }
    return { value: rows.reduce((s, r) => s + parts.reduce((t, [, pick]) => t + (pick(r) ?? 0), 0), 0), partial: false }
  }

  const receivables = sum(['totalReceivables', (r) => r.totalReceivables])
  const overdue = sum(['totalOverdue', (r) => r.totalOverdue])
  const advance = sum(['advancePayment', (r) => r.advancePayment])

  // "Aged over 120 days" is the three oldest buckets added together, exactly as
  // the Fabric app defines it.
  const over120 = sum(['d121To180', (r) => r.d121To180], ['d181To365', (r) => r.d181To365], ['dOver365', (r) => r.dOver365])

  const share = (part: ShownTotal) =>
    part.value !== null && receivables.value
      ? `${((part.value / receivables.value) * 100).toFixed(1)}%${part.partial || receivables.partial ? '*' : ''} of receivables`
      : undefined

  /* The unit for the whole strip.
     In USD every tile is dollars by construction. In local currency the strip
     spans whatever the caller can see, so it carries a code only when EVERY
     root row agrees on one -- a single-company user gets "SAR 45.34B", and
     anyone spanning currencies gets a bare number rather than a wrong symbol. */
  const codes = new Set(rows.map((r) => r.currencyCode))
  const code = codes.size === 1 ? [...codes][0] : null

  const show = (t: ShownTotal) =>
    t.value === null ? (v2 ? WITHHELD : '—') : `${compact(t.value, currency, code)}${t.partial ? '*' : ''}`
  const full = (t: ShownTotal) =>
    t.value === null ? (v2 ? WITHHELD_TITLE : 'Not shown to you')
      : t.partial ? `${money(t.value, currency, code)}. ${PARTIAL_TITLE}.` : money(t.value, currency, code)

  return (
    <div className="stat-strip">
      {/* THE HEADLINE FIGURE, AND WHAT IT IS.
          The grand total is kept and labelled rather than suppressed —
          decided 5 Aug 2026, to match the report being replaced rather than
          change a number people already read during a cutover.

          The label must therefore track the currency. It previously said
          "mixed local currencies" unconditionally, which was wrong in USD
          mode: converted, this total is a real quantity of dollars and the
          caveat would have told the reader to distrust the one figure on the
          page they can trust.

          22 currencies and 40 companies, measured against the live clone on
          5 Aug 2026 — not an estimate. */}
      <StatTile
        surface="kpi"
        measureKey="totalReceivables"
        label="Total receivables"
        value={show(receivables)}
        fullValue={full(receivables)}
        secondary={
          isConverted(currency)
            ? `Open items · ${currencyShort(currency)}`
            : code
              ? `Open items · ${code}, as booked`
              : 'Open items · adds up to several local currencies — switch to USD to compare'
        }
        tone="accent"
        dense
      />
      <StatTile
        surface="kpi"
        measureKey="totalOverdue"
        label="Total overdue"
        value={show(overdue)}
        fullValue={full(overdue)}
        secondary={share(overdue)}
        tone="bad"
        dense
      />
      <StatTile
        surface="kpi"
        measureKey="over120"
        label="Aged over 120 days"
        value={show(over120)}
        fullValue={full(over120)}
        secondary={share(over120)}
        dense
      />
      <StatTile
        surface="kpi"
        measureKey="advancePayment"
        label="Advance payments"
        value={show(advance)}
        fullValue={full(advance)}
        secondary="Customer credits on account"
        dense
      />
    </div>
  )
}
